import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './SiteDetails.css';
import MetricsPanel from '../components/MetricsPanel';
import ThresholdModal from '../components/ThresholdModal';
import { useSites } from '../context/SitesContext';
import { useWebSocket } from '../context/WebSocketContext';
import { fetchMetricsBySite } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';

const SiteDetails = () => {
  const { siteId } = useParams();
  const navigate = useNavigate();
  const { sites } = useSites();
  const { addListener } = useWebSocket();
  const [site, setSite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showThresholdModal, setShowThresholdModal] = useState(false);
  
  // Preserve scroll position across re-renders
  const scrollPositionRef = useRef(0);

  useEffect(() => {
    const loadSiteData = async (preserveScroll = false) => {
      try {
        // Only set loading if we don't have site data yet (initial load)
        if (!site) {
          setLoading(true);
        }
        
        // Preserve scroll position before updating
        const scrollY = preserveScroll ? window.scrollY : undefined;
        
        // Use sites from context instead of fetching
        const siteData = sites.find(s => s.id === parseInt(siteId));
        if (!siteData) {
          setError('Site not found');
          setLoading(false);
          return;
        }

        // Only fetch metrics, not sites
        const metrics = await fetchMetricsBySite(parseInt(siteId));

        setSite({
          ...siteData,
          ...metrics
        });
        setError(null);
        
        // Restore scroll position after update if we were preserving it
        if (preserveScroll && scrollY !== undefined) {
          // Use requestAnimationFrame to ensure DOM has updated
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollY);
          });
        }
      } catch (err) {
        setError('Failed to load site data');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    // Only load initial data if sites are available and we don't have site data yet
    if (sites.length > 0 && !site) {
      loadSiteData();
    }
    
    // Listen to WebSocket events from shared connection
    const removeListener = addListener((data) => {
      if (data.type === 'metrics_updated' && data.siteId === parseInt(siteId)) {
        // Preserve scroll position before updating
        scrollPositionRef.current = window.scrollY;
        
        // Immediately update site state with WebSocket data for instant UI update
        if (data.metrics) {
          setSite(prevSite => {
            if (!prevSite) return prevSite;
            // Create completely new objects to ensure React detects the change
            const updatedSite = {
              ...prevSite,
              ...data.metrics,
              // Ensure heartbeat is a new object reference
              heartbeat: data.metrics.heartbeat ? { ...data.metrics.heartbeat } : prevSite.heartbeat,
              // Preserve site info from context
              id: prevSite.id,
              name: prevSite.name,
              ip: prevSite.ip,
              location: prevSite.location
            };
            return updatedSite;
          });
          
          // Restore scroll position after state update
          requestAnimationFrame(() => {
            window.scrollTo(0, scrollPositionRef.current);
          });
        }
        // Then refresh full metrics in the background for accuracy
        // Only refresh if modal is not open to avoid disrupting user
        if (!showThresholdModal) {
          setTimeout(() => {
            loadSiteData(true); // preserveScroll = true
          }, 100);
        }
      } else if (data.type === 'polling_refresh') {
        // Refresh metrics only, not sites
        // Only refresh if modal is not open to avoid disrupting user
        if (!showThresholdModal) {
          loadSiteData(true); // preserveScroll = true
        }
      }
    });
    
    return () => {
      removeListener();
    };
    // Only depend on siteId and addListener, not sites array to avoid unnecessary re-renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, addListener, showThresholdModal]);

  // While the site page is open, periodically refresh metrics so Agent Down
  // (no new metrics WebSocket events) still updates alerts/status without a manual reload.
  useEffect(() => {
    if (!siteId || showThresholdModal) return undefined;
    const id = setInterval(async () => {
      try {
        const metrics = await fetchMetricsBySite(parseInt(siteId, 10));
        const siteData = sites.find(s => s.id === parseInt(siteId, 10));
        if (!siteData) return;
        scrollPositionRef.current = window.scrollY;
        setSite(prev => ({
          ...(prev || siteData),
          ...siteData,
          ...metrics,
          id: siteData.id,
          name: siteData.name,
          ip: siteData.ip
        }));
        requestAnimationFrame(() => {
          window.scrollTo(0, scrollPositionRef.current);
        });
      } catch (err) {
        console.error('Periodic site metrics refresh failed:', err);
      }
    }, 30000);
    return () => clearInterval(id);
  }, [siteId, showThresholdModal, sites]);
  
  // Preserve scroll position when site data updates
  useEffect(() => {
    if (site && scrollPositionRef.current > 0) {
      // Restore scroll position after render
      requestAnimationFrame(() => {
        window.scrollTo(0, scrollPositionRef.current);
      });
    }
  }, [site]);

  if (loading) {
    return (
      <div className="site-details-loading">
        <div className="spinner"></div>
        <p>Loading site details...</p>
      </div>
    );
  }

  if (error || !site) {
    return (
      <div className="site-details-error">
        <p>{error || 'Site not found'}</p>
        <button onClick={() => navigate('/')} className="back-btn">
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="site-details">
      <div className="site-details-header">
        <button onClick={() => navigate('/')} className="back-btn">
          ← Back to Dashboard
        </button>
        <h1 className="site-details-title">{site.name}</h1>
        <div className="site-details-actions">
          <button 
            onClick={() => navigate(`/trends/${siteId}/load_average`)}
            className="load-trend-btn"
            title="View Load Average Trend"
          >
            📈 Trends
          </button>
          <button 
            onClick={() => setShowThresholdModal(true)} 
            className="threshold-settings-btn"
            title="Configure Alert Thresholds"
          >
            ⚙️ Threshold Settings
          </button>
        </div>
      </div>

      <div className="site-details-content">
        <div className="panel">
          <h2 className="panel-title">Site Information</h2>
          <div className="site-info-grid">
            <div className="site-info-item">
              <span className="site-info-label">Site ID:</span>
              <span className="site-info-value">{site.id}</span>
            </div>
            <div className="site-info-item">
              <span className="site-info-label">IP Address:</span>
              <span 
                className="site-info-value"
                style={{
                  color: site.ip && site.publicIp 
                    ? (site.ip.trim() === site.publicIp.trim() ? '#10b981' : '#fbbf24')
                    : 'inherit'
                }}
              >
                {site.ip}
              </span>
            </div>
            {site.timestamp && (
              <div className="site-info-item">
                <span className="site-info-label">Last Update:</span>
                <span className="site-info-value">
                  {formatDateTime(site.timestamp)}
                </span>
              </div>
            )}
            {site.publicIp && (
              <div className="site-info-item">
                <span className="site-info-label">Agent Public IP:</span>
                <span className="site-info-value">{site.publicIp}</span>
              </div>
            )}
            {site.myParkVersion && (
              <div className="site-info-item">
                <span className="site-info-label">MyPark Version:</span>
                <span className="site-info-value">{site.myParkVersion}</span>
              </div>
            )}
            {site.heartbeat && (
              <div className="site-info-item">
                <span className="site-info-label">Agent Version:</span>
                <span className="site-info-value">{site.agentVersion || '1.0.0'}</span>
              </div>
            )}
          </div>
        </div>

        <MetricsPanel 
          site={site} 
          onAlertResolved={async () => {
            // Reload site data after alert is resolved
            try {
              const metrics = await fetchMetricsBySite(parseInt(siteId));
              const siteData = sites.find(s => s.id === parseInt(siteId));
              if (siteData) {
                setSite({
                  ...siteData,
                  ...metrics
                });
              }
            } catch (err) {
              console.error('Failed to reload site data:', err);
            }
          }}
        />
      </div>

      {showThresholdModal && site && (
        <ThresholdModal
          key={`threshold-modal-${siteId}`}
          siteId={parseInt(siteId)}
          siteName={site.name}
          onClose={() => setShowThresholdModal(false)}
          onSave={async () => {
            // Reload metrics after saving thresholds
            try {
              const metrics = await fetchMetricsBySite(parseInt(siteId));
              const siteData = sites.find(s => s.id === parseInt(siteId));
              if (siteData) {
                setSite({
                  ...siteData,
                  ...metrics
                });
              }
            } catch (err) {
              console.error('Failed to reload site data:', err);
            }
          }}
        />
      )}
    </div>
  );
};

export default SiteDetails;

